/**
 * VoiceControls - Main control buttons for voice mode.
 *
 * Includes push-to-talk button, mute toggle, and end call.
 */

import {
  Mic,
  MicOff,
  PhoneOff,
  Volume2,
  VolumeX,
} from 'lucide-react'

interface VoiceControlsProps {
  isRecording: boolean
  isMuted: boolean
  onPushToTalk: (pressed: boolean) => void
  onMuteToggle: () => void
  onEndCall: () => void
  mode: 'push-to-talk' | 'hands-free'
}

export function VoiceControls({
  isRecording,
  isMuted,
  onPushToTalk,
  onMuteToggle,
  onEndCall,
  mode,
}: VoiceControlsProps) {
  return (
    <div className="flex items-center justify-center gap-4">
      {/* Mute button */}
      <button
        onClick={onMuteToggle}
        className={`p-3 rounded-full transition-all ${
          isMuted
            ? 'bg-red-500/20 text-red-400 border border-red-500/30'
            : 'bg-surface border border-surface-border text-text-secondary hover:bg-surface-hover'
        }`}
        aria-label={isMuted ? 'Unmute' : 'Mute'}
      >
        {isMuted ? (
          <VolumeX className="w-6 h-6" />
        ) : (
          <Volume2 className="w-6 h-6" />
        )}
      </button>

      {/* End call button */}
      <button
        onClick={onEndCall}
        className="p-5 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors shadow-lg shadow-red-500/30"
        aria-label="End voice session"
      >
        <PhoneOff className="w-8 h-8" />
      </button>

      {/* Push-to-talk mic button */}
      <button
        onMouseDown={() => onPushToTalk(true)}
        onMouseUp={() => onPushToTalk(false)}
        onMouseLeave={() => onPushToTalk(false)}
        onTouchStart={() => onPushToTalk(true)}
        onTouchEnd={() => onPushToTalk(false)}
        disabled={mode === 'hands-free'}
        className={`p-3 rounded-full transition-all ${
          isRecording
            ? 'bg-accent text-white scale-110 shadow-lg shadow-accent/30'
            : 'bg-surface border border-surface-border text-text-secondary hover:bg-surface-hover'
        } disabled:opacity-50`}
        aria-label={isRecording ? 'Speaking' : 'Hold to talk'}
      >
        {isRecording ? (
          <Mic className="w-6 h-6" />
        ) : (
          <MicOff className="w-6 h-6" />
        )}
      </button>
    </div>
  )
}

export default VoiceControls
